import { isCallable } from '.'

/**
 * A factory function that returns a generic type-guard function to check if a value exists in the passed object (as const).
 * Useful for when you need to combine static & runtime type-guards in a single function.
 *
 * @param {object} obj - Some object to check for the existence of a target value
 * @returns {boolean} Returns true if the value exists in the object
 * @example
 * import { isValueOfObject } from '@afterpaytouch/core'
 *
 * const SomeObject = {
 *  foo: 'bar',
 * } as const // Use 'as const' to allow TS to infer the type of the object
 *
 * const isValidSomeObject = isValueOfObject(SomeObject)
 *
 * isValidSomeObject('bar') // true
 * isValidSomeObject('foo') // false
 *
 */
export const isValueOfObject = <T extends object>(obj: T): ((value: any) => value is (typeof obj)[keyof typeof obj]) => {
  return (value: any): value is (typeof obj)[keyof typeof obj] => {
    return Object.values(obj).includes(value)
  }
}

const isNotObject = (object: any): boolean => {
  return typeof object !== 'object' || object === null
}

export const isEmptyObject = (x: any): boolean => {
  if (x !== null && !Array.isArray(x) && typeof x === 'object' && Object.keys(x).length === 0) {
    return true
  }
  return false
}
export const isValidObject = (object: any): object is object =>
  !!object && (Object.keys(object).length > 0 || Object.keys(Object.getPrototypeOf(object)).length > 0) && object.constructor === Object

/**
 * Compares two objects are the same by the enumerable primitive properties
 *
 * @param {object} objectA
 * @param {object} objectB
 * @param {boolean} ignoreEmpty //Treat null, empty and undefined values the same
 * @param {array} ignoreKeys // ignore certain keys from comparison
 * @returns {boolean}
 */
export const compareObjects = (objectA: any, objectB: any, ignoreEmpty = false, ignoreKeys: string[] = []): boolean => {
  if (objectA === objectB) {
    return true
  }

  if (isNotObject(objectA) || isNotObject(objectB)) {
    return false
  }

  const keysA = Object.keys(objectA).filter((key) => !ignoreKeys.includes(key))
  const keysB = new Set(Object.keys(objectB).filter((key) => !ignoreKeys.includes(key)))

  if (keysA.length !== keysB.size) {
    return false
  }

  for (const key of keysA) {
    const itemA: any = ignoreEmpty ? objectA[key as keyof object] ?? '' : objectA[key as keyof object]
    const itemB: any = ignoreEmpty ? objectB[key as keyof object] ?? '' : objectB[key as keyof object]

    if (!keysB.has(key)) {
      return false
    }

    if (isCallable(itemA) || isCallable(itemB)) {
      if (itemA.toString() !== itemB.toString()) {
        return false
      }
    } else {
      if (!compareObjects(itemA, itemB)) {
        return false
      }
    }
  }

  return true
}
